import React, { useState } from 'react';
import { Mission } from '@/lib/gamification/types';
import { supabase } from '@/lib/supabase';
import { assignMissionToUser } from '@/lib/gamification/missionService';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'react-hot-toast';
import { Loader2, UserPlus, X } from 'lucide-react';

interface AssignMissionModalProps {
  mission: Mission;
  isOpen: boolean;
  onClose: () => void;
}

// Função para verificar se uma string é um UUID v4
const isUUID = (str: string) => {
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
  return uuidRegex.test(str);
}

const AssignMissionModal: React.FC<AssignMissionModalProps> = ({ mission, isOpen, onClose }) => {
  const [identifiersInput, setIdentifiersInput] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAssign = async () => {
    if (!identifiersInput.trim()) {
      setError('Por favor, insira pelo menos um ID ou nome de usuário.');
      return;
    }
    setError(null);
    setIsAssigning(true);

    const identifiers = identifiersInput.split(/[\s,]+/).filter(id => id.trim() !== '');
    let successCount = 0;
    let failureCount = 0;
    let notFoundCount = 0;

    for (const identifier of identifiers) {
      let userIdToAssign: string | null = null;
      try {
        if (isUUID(identifier)) {
          userIdToAssign = identifier;
        } else {
          // Buscar usuário pelo username na tabela 'profiles'
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id')
            .eq('username', identifier)
            .single();

          if (profileError || !profile) {
            console.warn(`Usuário com username "${identifier}" não encontrado:`, profileError);
            notFoundCount++;
            continue; // Pula para o próximo identificador
          }
          userIdToAssign = profile.id;
        }

        if (userIdToAssign) {
          const result = await assignMissionToUser(supabase, userIdToAssign, mission.id, null);
          if (result) {
            successCount++;
          } else {
            failureCount++; // Falha na atribuição da missão em si
          }
        }
      } catch (err) {
        console.error(`Falha ao processar atribuição para "${identifier}":`, err);
        failureCount++; // Falha genérica no processamento
      }
    }

    setIsAssigning(false);
    let messages: string[] = [];
    if (successCount > 0) {
      messages.push(`${successCount} missão(ões) "${mission.title}" atribuída(s) com sucesso.`);
    }
    if (failureCount > 0) {
      messages.push(`${failureCount} falha(s) na atribuição.`);
    }
    if (notFoundCount > 0) {
      messages.push(`${notFoundCount} usuário(s) não encontrado(s).`);
    }

    const finalMessage = messages.join(' ');

    if (messages.length > 0) {
      if (successCount > 0 && failureCount === 0 && notFoundCount === 0) {
        toast.success(finalMessage);
      } else if (successCount === 0 && (failureCount > 0 || notFoundCount > 0)){
        toast.error(finalMessage + " Verifique os logs.");
      } else { // Casos mistos (sucesso com algumas falhas/não encontrados)
        toast.success(finalMessage + " Verifique os logs para mais detalhes."); // Usar success mas indicar que há mais infos
      }
    } else if (identifiers.length > 0) { 
        toast.error("Nenhum usuário processado. Verifique os identificadores e os logs.");
    }
    
    if (notFoundCount === 0 && identifiers.length > 0) { // Fecha se todos os identificadores foram encontrados (mesmo que alguns falhem na atribuição)
        setIdentifiersInput(''); 
        onClose();
    } else if (notFoundCount > 0) {
        // Mantém o modal aberto e o input preenchido se houver usuários não encontrados
        // para que o usuário possa corrigir.
        setError(`${notFoundCount} usuário(s) não encontrado(s). Por favor, verifique os nomes/IDs.`);
    } else if (identifiers.length === 0) { // Se o input estava vazio ou só com espaços
        onClose(); // Fecha se não havia nada para processar
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(openState) => !openState && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Atribuir Missão: {mission.title}</DialogTitle>
          <DialogDescription>
            Insira os IDs (UUIDs) ou nomes de usuário (separados por vírgula ou espaço) para atribuir esta missão.
            A missão tem frequência: <strong>{mission.frequency}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <Label htmlFor="identifiers">IDs ou Nomes de Usuário</Label>
            <Textarea
              id="identifiers"
              value={identifiersInput}
              onChange={(e) => setIdentifiersInput(e.target.value)}
              placeholder="ID_usuario1, username2, ID_usuario3, ..."
              rows={3}
              disabled={isAssigning}
            />
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>
        </div>
        <DialogFooter className="sm:justify-start">
          <Button onClick={handleAssign} disabled={isAssigning || !identifiersInput.trim()}>
            {isAssigning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Atribuindo...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Confirmar Atribuição
              </>
            )}
          </Button>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isAssigning}>
              Cancelar
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignMissionModal; 